import { memo, useState } from "react";
import styled from "styled-components";
import { Tooltip, message } from "antd";
import { CopyOutlined, CheckOutlined } from "@ant-design/icons";

/**
 * Props for UUIDViewer component
 * 
 * @example
 * ```tsx
 * <UUIDViewer uuid="550e8400-e29b-41d4-a716-446655440000" />
 * <UUIDViewer uuid="550e8400-e29b-41d4-a716-446655440000" displayMode="short" />
 * ```
 */
export interface UUIDViewerProps {
  uuid?: string | null;
  displayMode?: 'full' | 'short';
  className?: string;
  showTooltip?: boolean;
}

/**
 * UUIDViewer - Interactive UUID display component with copy functionality
 * 
 * Displays UUIDs in two modes:
 * - Full: Complete UUID (e.g., "550e8400-e29b-41d4-a716-446655440000")
 * - Short: First 8 characters with ellipsis (e.g., "550e8400...")
 * 
 * Click copy icon to copy full UUID to clipboard.
 * Hover to see full UUID in tooltip (when in short mode).
 * 
 * @example
 * ```tsx
 * <UUIDViewer uuid={project.id} />
 * <UUIDViewer uuid={user.uuid} displayMode="short" />
 * ```
 */
const UUIDViewer = (props: UUIDViewerProps) => {
  const {
    uuid,
    displayMode = 'short',
    className,
    showTooltip = true
  } = props;

  const [copied, setCopied] = useState(false);

  if (!uuid) return <div className={className}>-</div>;

  const displayText = displayMode === 'short' 
    ? `...${uuid.slice(-6)}` 
    : uuid;

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    try {
      await navigator.clipboard.writeText(uuid);
      setCopied(true);
      message.success('Đã copy UUID vào clipboard');
      
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (err) {
      message.error('Không thể copy UUID');
      console.error('Copy failed:', err);
    }
  };

  const content = (
    <UI_Wrapper className={className}>
      <UI_Text>{displayText}</UI_Text>
      <UI_CopyButton onClick={handleCopy} $copied={copied}>
        {copied ? <CheckOutlined /> : <CopyOutlined />}
      </UI_CopyButton>
    </UI_Wrapper>
  );

  if (!showTooltip || displayMode === 'full') {
    return content;
  }

  return (
    <Tooltip title={uuid}>
      {content}
    </Tooltip>
  );
};

export default memo(UUIDViewer);

const UI_Wrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 6px;
  border-radius: 4px;
  transition: all 0.2s ease;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 13px;
  max-width: 100%;
  overflow: hidden;

  &:hover {
    background-color: #f0f0f0;
  }
`;

const UI_Text = styled.span`
  display: inline-block;
  user-select: all;
  color: #666;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex-shrink: 1;
  min-width: 0;
`;

const UI_CopyButton = styled.button<{ $copied: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  cursor: pointer;
  padding: 2px;
  border-radius: 3px;
  color: ${props => props.$copied ? '#52c41a' : '#1890ff'};
  transition: all 0.2s ease;
  font-size: 12px;

  &:hover {
    background-color: ${props => props.$copied ? '#f6ffed' : '#e6f7ff'};
  }

  &:active {
    transform: scale(0.95);
  }
`;
